import { useCallback, useEffect, useState } from 'react';
import { services as servicesApi, stores as storesApi } from '../api/endpoints';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { Loading, Empty, Modal, Can, money } from '../components/Common';

/**
 * Pricing.
 *
 * Two jobs: revise many rates at once (a seasonal % change), and set a
 * store-specific rate that overrides the catalogue price for one branch.
 */
export default function Pricing() {
  const toast = useToast();
  const { isSuperAdmin } = useAuth();

  const [items, setItems] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [store, setStore] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [overriding, setOverriding] = useState(null);

  const load = useCallback(async () => {
    try {
      const its = await servicesApi.items(store ? { store } : {});
      setItems(its.data.items);
    } catch (err) {
      toast.error('Could not load pricing', err.message);
    } finally {
      setLoading(false);
    }
  }, [store, toast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    storesApi.list({ limit: 100 }).then((r) => setStoreList(r.data.stores)).catch(() => {});
    document.title = 'Pricing · UrbanClean';
  }, []);

  const toggleSelect = (id) => {
    setSelected((list) => (list.includes(id) ? list.filter((i) => i !== id) : [...list, id]));
  };

  if (loading) return <Loading label="Loading pricing" />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Pricing</h1>
          <div className="sub">
            {store
              ? 'Showing the effective rates for the selected store'
              : 'Showing catalogue rates — pick a store to see its overrides'}
          </div>
        </div>
        <Can permission="pricing.update">
          <button className="btn btn-blue" disabled={selected.length === 0} onClick={() => setBulkOpen(true)}>
            Revise {selected.length || ''} selected
          </button>
        </Can>
      </div>

      <div className="toolbar">
        {isSuperAdmin ? (
          <select className="select" value={store} onChange={(e) => setStore(e.target.value)}>
            <option value="">Catalogue prices (all stores)</option>
            {storeList.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
          </select>
        ) : null}
        <button className="btn btn-sm btn-ghost" onClick={() => setSelected(items.map((i) => i._id))}>Select all</button>
        <button className="btn btn-sm btn-ghost" onClick={() => setSelected([])}>Clear</button>
      </div>

      <div className="card">
        {items.length === 0 ? <Empty title="No items to price" /> : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th style={{ width: 40 }}></th>
                  <th>Item</th><th>Category</th><th>Unit</th>
                  <th className="right">Price</th><th className="right">Express</th><th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item._id}>
                    <td>
                      <input type="checkbox" checked={selected.includes(item._id)} onChange={() => toggleSelect(item._id)} />
                    </td>
                    <td className="strong">{item.name}</td>
                    <td className="muted">{item.category?.name}</td>
                    <td className="muted">per {item.unit}</td>
                    <td className="right strong">{money(item.price)}</td>
                    <td className="right muted">{item.expressPrice ? money(item.expressPrice) : '—'}</td>
                    <td className="right">
                      {isSuperAdmin ? (
                        <Can permission="pricing.update">
                          <button className="btn btn-sm btn-ghost" onClick={() => setOverriding(item)}>Store rate</button>
                        </Can>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <BulkModal
        open={bulkOpen}
        count={selected.length}
        itemIds={selected}
        onClose={() => setBulkOpen(false)}
        onDone={() => { setBulkOpen(false); setSelected([]); load(); }}
      />

      <OverrideModal
        item={overriding}
        storeList={storeList}
        onClose={() => setOverriding(null)}
        onSaved={() => { setOverriding(null); load(); }}
      />
    </>
  );
}

function BulkModal({ open, count, itemIds, onClose, onDone }) {
  const toast = useToast();
  const [mode, setMode] = useState('percent');
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    if (value === '') return toast.error('Enter an amount');
    setBusy(true);
    try {
      const body = { itemIds };
      if (mode === 'percent') body.percent = Number(value);
      else body.flat = Number(value);

      const res = await servicesApi.bulkPrice(body);
      toast.success(res.message);
      onDone();
    } catch (err) {
      toast.error('Could not revise prices', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} title={`Revise ${count} item(s)`} onClose={onClose}
      footer={<>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={apply} disabled={busy}>{busy ? 'Applying…' : 'Apply revision'}</button>
      </>}>
      <div className="field">
        <label>Revision type</label>
        <select className="select" value={mode} onChange={(e) => setMode(e.target.value)}>
          <option value="percent">Percentage change</option>
          <option value="flat">Flat amount</option>
        </select>
      </div>
      <div className="field">
        <label>{mode === 'percent' ? 'Percent (use a negative number to reduce)' : 'Amount (negative to reduce)'}</label>
        <input className="input" type="number" value={value} onChange={(e) => setValue(e.target.value)} placeholder={mode === 'percent' ? 'e.g. 10 or -5' : 'e.g. 20 or -10'} />
        <div className="hint">Applies to the base price of every selected item.</div>
      </div>
    </Modal>
  );
}

function OverrideModal({ item, storeList, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState({ store: '', price: '', expressPrice: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (item) setForm({ store: storeList[0]?._id || '', price: item.price ?? '', expressPrice: item.expressPrice ?? '' });
  }, [item, storeList]);

  if (!item) return null;

  const save = async () => {
    if (!form.store || form.price === '') return toast.error('Pick a store and enter a price');
    setBusy(true);
    try {
      await servicesApi.storePrice(item._id, {
        store: form.store,
        price: Number(form.price),
        expressPrice: form.expressPrice === '' ? null : Number(form.expressPrice),
      });
      toast.success('Store rate saved');
      onSaved();
    } catch (err) {
      toast.error('Could not save', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open title={`Store rate — ${item.name}`} onClose={onClose}
      footer={<>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save rate'}</button>
      </>}>
      <div className="field">
        <label>Store</label>
        <select className="select" value={form.store} onChange={(e) => setForm((f) => ({ ...f, store: e.target.value }))}>
          {storeList.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
        </select>
        <div className="hint">This rate replaces the catalogue price for orders routed to that store.</div>
      </div>
      <div className="row">
        <div className="field"><label>Price</label><input className="input" type="number" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} /></div>
        <div className="field"><label>Express price</label><input className="input" type="number" value={form.expressPrice} onChange={(e) => setForm((f) => ({ ...f, expressPrice: e.target.value }))} /></div>
      </div>
    </Modal>
  );
}
